import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Typography,
  Button,
  alpha,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  InputAdornment,
  IconButton,
  Grid,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  ReceiptLong as BillIcon,
  Search as SearchIcon,
  Verified as VerifiedIcon,
  Close as CloseIcon,
  School as SchoolIcon,
  Phone as PhoneIcon,
  Place as PlaceIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import apiService from "../../services/apiService";
import { crmColors } from "../../theme/colors";

const formatMoney = (value, currency = "INR") => {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  const prefix = currency === "INR" || !currency ? "₹" : `${currency} `;
  return `${prefix}${n.toLocaleString("en-IN")}`;
};

const fieldSx = { borderRadius: "10px", bgcolor: "#fff" };

const SectionHead = ({ step, title, hint, sx }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.15, ...sx }}>
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "7px",
        display: "grid",
        placeItems: "center",
        fontSize: 11,
        fontWeight: 800,
        color: "#fff",
        background: `linear-gradient(145deg, ${crmColors.primary.main}, ${crmColors.hero.start})`,
        boxShadow: `0 2px 6px ${alpha(crmColors.primary.main, 0.28)}`,
        flexShrink: 0,
      }}
    >
      {step}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: crmColors.text.primary,
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  </Stack>
);

const SectionPanel = ({ children, tone = "default" }) => {
  const border =
    tone === "success"
      ? alpha(crmColors.success.main, 0.22)
      : tone === "energy"
        ? alpha(crmColors.energy.main, 0.22)
        : crmColors.border.default;
  const bg =
    tone === "success"
      ? `linear-gradient(160deg, ${alpha(crmColors.success.main, 0.07)} 0%, ${alpha("#fff", 0.95)} 55%)`
      : tone === "energy"
        ? `linear-gradient(160deg, ${alpha(crmColors.energy.main, 0.07)} 0%, ${alpha("#fff", 0.95)} 55%)`
        : `linear-gradient(180deg, ${alpha(crmColors.primary.main, 0.03)} 0%, ${crmColors.background.paper} 100%)`;
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 1.75 },
        borderRadius: "14px",
        border: `1px solid ${border}`,
        background: bg,
        boxShadow: `0 1px 0 ${alpha("#fff", 0.8)} inset`,
      }}
    >
      {children}
    </Box>
  );
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const parseLocalDate = (iso) => {
  if (!iso) return null;
  const parts = String(iso).slice(0, 10).split("-").map(Number);
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  d.setHours(0, 0, 0, 0);
  return d;
};

const toISODate = (d) => {
  const date = d instanceof Date ? d : parseLocalDate(d);
  if (!date || Number.isNaN(date.getTime())) return todayISO();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

//Created By Cursor — EMI options for Add New Bill (1 = No EMI)
const EMI_OPTIONS = [
  { value: 1, label: "No EMI" },
  { value: 2, label: "2 EMI" },
  { value: 3, label: "3 EMI" },
  { value: 4, label: "4 EMI" },
  { value: 5, label: "5 EMI" },
  { value: 6, label: "6 EMI" },
  { value: 9, label: "9 EMI" },
  { value: 12, label: "12 EMI" },
];

//Created By Cursor — plan months → billing cycle + priced amount
const PLAN_OPTIONS = [
  { value: 3, label: "3 Months", months: 3 },
  { value: 6, label: "6 Months", months: 6 },
  { value: 12, label: "1 Year", months: 12 },
  { value: 24, label: "2 Years", months: 24 },
  { value: 36, label: "3 Years", months: 36 },
  { value: 48, label: "4 Years", months: 48 },
  { value: 60, label: "5 Years", months: 60 },
];
const PLAN_MONTH_VALUES = PLAN_OPTIONS.map((o) => o.months);

const emptyBillForm = () => ({
  school_code: "",
  product_code: "",
  order_type: "New",
  tax_id: "",
  promo_code: "",
  installment_count: 1,
  emi_rows: [],
  emi_rows_touched: false,
  plan_months: 12,
  plan_months_touched: false,
  discount_mode: "none",
  discount_value: "",
  apply_demo_credit: true,
  order_date: todayISO(),
  billing_start: todayISO(),
  billing_start_touched: false,
  record_payment: true,
  payment_amount: "",
  payment_method: "Online",
  payment_date: todayISO(),
  payment_reference: "",
  payment_remark: "",
  payment_amount_touched: false,
  renewal_rate: "",
  renewal_rate_touched: false,
});

const addMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + Number(months || 0));
  return d;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + Number(days || 0));
  return d;
};

const formatShortDate = (d) => {
  if (!d || Number.isNaN(d.getTime?.() ?? d)) return "—";
  const date = d instanceof Date ? d : parseLocalDate(d) || new Date(d);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

//Created By Cursor — default EMI rows (API: +30 days; last amount absorbs remainder)
const buildDefaultEmiRows = (total, count, orderDateIso) => {
  const n = Math.max(1, Number(count) || 1);
  const amountTotal = Number(total);
  if (n <= 1 || !Number.isFinite(amountTotal) || amountTotal <= 0) return [];
  const orderD = parseLocalDate(orderDateIso) || parseLocalDate(todayISO());
  const base = Math.floor((amountTotal / n) * 100) / 100;
  let allocated = 0;
  const rows = [];
  for (let i = 1; i <= n; i += 1) {
    const amount =
      i === n ? Math.round((amountTotal - allocated) * 100) / 100 : base;
    allocated = Math.round((allocated + amount) * 100) / 100;
    rows.push({
      seq: i,
      due_date: toISODate(addDays(orderD, (i - 1) * 30)),
      amount: String(amount),
    });
  }
  return rows;
};

//Created By Cursor — Cancelled / Upgraded are not live billed
const isSubscriptionCancelledOrUpgraded = (p) => {
  if (!p) return false;
  const subSt = String(p.subscription_status || "").toLowerCase();
  const display = String(p.display_status || "").toLowerCase();
  return (
    subSt === "cancelled" ||
    subSt === "canceled" ||
    subSt === "upgraded" ||
    display === "cancelled" ||
    display === "canceled" ||
    display === "upgraded"
  );
};

//Created By Cursor — billed only when a live subscription exists (Active alone is not enough)
const hasProductSubscription = (p) => {
  if (!p) return false;
  if (isSubscriptionCancelledOrUpgraded(p)) return false;
  return !!(
    p.subscription_id ||
    p.service_end_date ||
    p.covered_by_product
  );
};

//Created By Cursor — Grace / Expired / Active+live sub = billed; Cancelled → New allowed
const isProductAccountBilled = (p) => {
  if (!p) return false;
  if (isSubscriptionCancelledOrUpgraded(p)) return false;
  const name = String(p.product_status_name || "").toLowerCase();
  const display = String(p.display_status || "");
  if (
    name === "grace" ||
    name === "expired" ||
    display === "Grace" ||
    display === "Expired"
  ) {
    return true;
  }
  return hasProductSubscription(p);
};

//Created By Cursor — pick default billed product (prefer Full System TM004)
const pickDefaultBilledProductCode = (list = []) => {
  const billed = (list || []).filter(isProductAccountBilled);
  if (!billed.length) return "";
  const weberp = billed.find((p) => p.product_code === "TM004");
  if (weberp) return "TM004";
  const android = billed.find((p) => p.product_code === "TM001");
  if (android) return "TM001";
  return billed[0].product_code || "";
};

//Created By Cursor — Add New Bill dialog (BillDesk / Renewals deep link)
const MdlAddNewBill = ({
  open,
  onClose,
  onCreated,
  products = [],
  taxes = [],
  payMethods = [],
  initialSchoolCode = "",
  initialProductCode = "",
}) => {
  const [saving, setSaving] = useState(false);
  const [billForm, setBillForm] = useState(emptyBillForm);
  const [verifying, setVerifying] = useState(false);
  const [verifiedSchool, setVerifiedSchool] = useState(null);
  const [verifyError, setVerifyError] = useState("");
  const [seedToken, setSeedToken] = useState(0);

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  //Created By Cursor — verify school; optional overrides for Renewals deep link
  const handleVerifySchool = async (codeOverride = "", preferredProduct = "") => {
    const override =
      typeof codeOverride === "string" ? codeOverride : "";
    const code = String(override || billForm.school_code || "").trim();
    if (!code) {
      toast.error("Enter a school code first");
      return;
    }
    setVerifying(true);
    setVerifyError("");
    setVerifiedSchool(null);
    const result = await apiService.getSchoolDetailForBill(code);
    setVerifying(false);
    if (result.success && result.data?.school) {
      setVerifiedSchool(result.data);
      const schoolProducts = result.data.products || [];
      const defaultProduct = pickDefaultBilledProductCode(schoolProducts);
      const preferred = String(preferredProduct || "").trim().toUpperCase();
      const preferredOk =
        preferred &&
        schoolProducts.some(
          (p) =>
            String(p.product_code || "").toUpperCase() === preferred &&
            isProductAccountBilled(p)
        );
      const chosenProduct = preferredOk
        ? preferred
        : defaultProduct || preferred || "";
      const isBilled = !!chosenProduct || !!defaultProduct;
      setBillForm((f) => ({
        ...f,
        school_code: result.data.school.school_code || code,
        product_code: chosenProduct || f.product_code || "",
        order_type: isBilled ? "Renewal" : "New",
        apply_demo_credit: !isBilled,
        billing_start_touched: false,
      }));
      toast.success(
        isBilled
          ? `School verified · billed product ${chosenProduct || defaultProduct} selected`
          : "School verified"
      );
    } else {
      setVerifyError(result.error || result.message || "School not found");
      toast.error(result.error || "School not found");
    }
  };

  //Created By Cursor — reset form when dialog opens
  useEffect(() => {
    if (!open) return;
    const defaultTax =
      taxes.find((t) => Number(t.isDefault) === 1)?.tax_id || taxes[0]?.tax_id || "";
    const defaultPay =
      payMethods[0]?.payment_mode_id != null
        ? String(payMethods[0].payment_mode_id)
        : "Online";
    const today = todayISO();
    const school = String(initialSchoolCode || "").trim();
    const product = String(initialProductCode || "").trim();
    setBillForm({
      ...emptyBillForm(),
      school_code: school,
      product_code: product,
      tax_id: defaultTax,
      payment_method: defaultPay,
      payment_date: today,
      order_date: today,
      billing_start: today,
      billing_start_touched: false,
    });
    setVerifiedSchool(null);
    setVerifyError("");
    setSeedToken((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init once per open
  }, [open]);

  //Created By Cursor — auto-verify after seed when opened with school code
  useEffect(() => {
    if (!open || !seedToken) return;
    const school = String(initialSchoolCode || "").trim();
    const product = String(initialProductCode || "").trim();
    if (!school) return;
    void handleVerifySchool(school, product);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seedToken]);

  const handleSchoolCodeChange = (value) => {
    setBillForm((f) => ({ ...f, school_code: value }));
    if (
      verifiedSchool &&
      value.trim().toUpperCase() !== (verifiedSchool.school?.school_code || "").toUpperCase()
    ) {
      setVerifiedSchool(null);
      setVerifyError("");
      setBillForm((f) => ({ ...f, school_code: value, product_code: "" }));
    }
  };

  const selectedProduct = useMemo(
    () => products.find((r) => r.product_code === billForm.product_code),
    [products, billForm.product_code]
  );

  const schoolVerified =
    verifiedSchool?.school &&
    (verifiedSchool.school.school_code || "").toUpperCase() ===
      billForm.school_code.trim().toUpperCase();

  const demoDaysLeft = useMemo(() => {
    if (!schoolVerified || !billForm.product_code) return 0;
    const p = (verifiedSchool.products || []).find(
      (x) => x.product_code === billForm.product_code
    );
    return Math.max(0, Number(p?.demo_days_left || 0));
  }, [schoolVerified, verifiedSchool, billForm.product_code]);

  //Created By Cursor — billed school for selected product → Renewal
  const billedRenewalInfo = useMemo(() => {
    const empty = {
      isBilled: false,
      renewFromEnd: null,
      product: null,
      status: null,
      serviceStart: null,
      serviceEnd: null,
      graceEnd: null,
      graceDaysLeft: null,
      expiryLabel: null,
      timelineLabel: null,
    };
    if (!schoolVerified || !billForm.product_code) {
      return empty;
    }
    const schoolProducts = verifiedSchool.products || [];
    const pc = billForm.product_code;
    let p = schoolProducts.find((x) => x.product_code === pc) || null;
    if ((!p || !hasProductSubscription(p)) && pc === "TM001") {
      const cover = schoolProducts.find((x) => x.product_code === "TM004") || null;
      //Created By Cursor — TM004 covers TM001 only when TM004 has a subscription
      if (cover && hasProductSubscription(cover)) {
        p = { ...(p || {}), ...cover, product_code: pc, covered_by_product: "TM004" };
      }
    }
    if (!p) {
      return empty;
    }
    const name = String(p.product_status_name || "").toLowerCase();
    const display = String(p.display_status || "");
    const isBilled = isProductAccountBilled(p);
    if (!isBilled) {
      return { ...empty, product: p };
    }

    const serviceStart = p.service_start_date ? parseLocalDate(p.service_start_date) : null;
    const serviceEnd = p.service_end_date
      ? parseLocalDate(p.service_end_date)
      : verifiedSchool.summary?.service_expiry
        ? parseLocalDate(verifiedSchool.summary.service_expiry)
        : null;
    const graceEnd = p.grace_end_date
      ? parseLocalDate(p.grace_end_date)
      : verifiedSchool.summary?.grace_end_date
        ? parseLocalDate(verifiedSchool.summary.grace_end_date)
        : null;
    const now = new Date();
    let status = "Active";
    let graceDaysLeft =
      p.grace_days_left != null
        ? Number(p.grace_days_left)
        : verifiedSchool.summary?.grace_days_left != null
          ? Number(verifiedSchool.summary.grace_days_left)
          : null;
    if (display === "Grace" || name === "grace") {
      status = "Grace";
    } else if (display === "Expired" || name === "expired") {
      status = "Expired";
    } else if (
      serviceEnd &&
      !Number.isNaN(serviceEnd.getTime()) &&
      serviceEnd < now &&
      graceEnd &&
      !Number.isNaN(graceEnd.getTime()) &&
      graceEnd >= now
    ) {
      status = "Grace";
      if (graceDaysLeft == null) {
        graceDaysLeft = Math.max(
          0,
          Math.floor((graceEnd.getTime() - now.getTime()) / 86400000)
        );
      }
    } else if (
      (graceEnd && !Number.isNaN(graceEnd.getTime()) && graceEnd < now) ||
      (serviceEnd &&
        !Number.isNaN(serviceEnd.getTime()) &&
        serviceEnd < now &&
        (!graceEnd || Number.isNaN(graceEnd.getTime())))
    ) {
      status = "Expired";
      graceDaysLeft = 0;
    }

    let renewFromEnd = null;
    if (serviceEnd && !Number.isNaN(serviceEnd.getTime())) {
      if (status === "Active" || status === "Grace") {
        renewFromEnd = toISODate(serviceEnd);
      }
    }

    const expiryLabel = serviceEnd ? formatShortDate(serviceEnd) : "—";
    let timelineLabel = status;
    if (status === "Active" && serviceStart && serviceEnd) {
      timelineLabel = `${formatShortDate(serviceStart)} → ${formatShortDate(serviceEnd)}`;
    } else if (status === "Active" && serviceEnd) {
      timelineLabel = `Active · ends ${formatShortDate(serviceEnd)}`;
    } else if (status === "Grace") {
      const gLeft =
        graceDaysLeft != null
          ? `${graceDaysLeft} day${graceDaysLeft === 1 ? "" : "s"} left`
          : "in grace";
      timelineLabel = graceEnd
        ? `Grace · ${gLeft} · until ${formatShortDate(graceEnd)}`
        : `Grace · ${gLeft}`;
    } else if (status === "Expired") {
      timelineLabel = serviceEnd
        ? `Expired · ended ${formatShortDate(serviceEnd)}`
        : "Expired";
    }

    return {
      isBilled: true,
      renewFromEnd,
      product: p,
      status,
      serviceStart,
      serviceEnd,
      graceEnd,
      graceDaysLeft,
      expiryLabel,
      timelineLabel,
    };
  }, [schoolVerified, verifiedSchool, billForm.product_code]);

  const schoolIsBilled = useMemo(() => {
    if (!schoolVerified) return false;
    return (verifiedSchool.products || []).some((p) => isProductAccountBilled(p));
  }, [schoolVerified, verifiedSchool]);

  const canUpgradeToFullSystem = useMemo(() => {
    if (!schoolVerified) return false;
    const schoolProducts = verifiedSchool.products || [];
    const android = schoolProducts.find((p) => p.product_code === "TM001");
    const weberp = schoolProducts.find((p) => p.product_code === "TM004");
    return isProductAccountBilled(android) && !isProductAccountBilled(weberp);
  }, [schoolVerified, verifiedSchool]);

  useEffect(() => {
    if (!open || !schoolVerified || !schoolIsBilled) return;
    if (billForm.order_type === "Upgrade" && canUpgradeToFullSystem) {
      if (billForm.product_code !== "TM004") {
        setBillForm((f) => ({ ...f, product_code: "TM004", apply_demo_credit: false }));
      }
      return;
    }
    if (billForm.order_type === "New" || billForm.order_type === "Upgrade") {
      const def =
        pickDefaultBilledProductCode(verifiedSchool?.products || []) ||
        billForm.product_code;
      setBillForm((f) => ({
        ...f,
        order_type: "Renewal",
        product_code: def || f.product_code,
        apply_demo_credit: false,
      }));
    }
  }, [
    open,
    schoolVerified,
    schoolIsBilled,
    billForm.order_type,
    billForm.product_code,
    canUpgradeToFullSystem,
    verifiedSchool,
  ]);

  const handleBillTypeChange = (nextType) => {
    if (schoolIsBilled && nextType === "New") {
      toast.error(
        "New subscription not allowed while billed. Cancel current subscription from School Details first."
      );
      return;
    }
    setBillForm((f) => {
      const patch = {
        ...f,
        order_type: nextType,
        apply_demo_credit: nextType === "New",
        billing_start_touched: false,
        renewal_rate_touched: false,
        plan_months_touched: false,
      };
      if (nextType === "Upgrade") {
        patch.product_code = "TM004";
        patch.apply_demo_credit = false;
      } else if (nextType === "Renewal") {
        const def = pickDefaultBilledProductCode(verifiedSchool?.products || []);
        if (def) patch.product_code = def;
        patch.apply_demo_credit = false;
      }
      return patch;
    });
  };

  const selectedTax = useMemo(
    () => taxes.find((t) => t.tax_id === billForm.tax_id),
    [taxes, billForm.tax_id]
  );

  const billPreview = useMemo(() => {
    if (!selectedProduct) return null;
    //Created By Cursor — Renewal prefers school subscription renewal_rate
    const schoolProd = (verifiedSchool?.products || []).find(
      (p) => p.product_code === billForm.product_code
    );
    const productRate = Number(
      selectedProduct.product_rate || schoolProd?.product_rate || 0
    );
    const catalogRenewal = Number(
      schoolProd?.renewal_rate ??
        selectedProduct.renewal_rate ??
        schoolProd?.product_rate ??
        selectedProduct.product_rate ??
        0
    );
    const renewalFromForm = Number(billForm.renewal_rate);
    const renewalRate =
      billForm.renewal_rate !== "" && !Number.isNaN(renewalFromForm) && renewalFromForm >= 0
        ? renewalFromForm
        : catalogRenewal;
    const isRenewal = billForm.order_type === "Renewal";
    //Created By Cursor — <12 mo pro-rata; 1yr = Y1 rate; 2yr+ = Y1 + renewal×(years−1)
    let cycleMonths = Number(billForm.plan_months) || 12;
    if (!PLAN_MONTH_VALUES.includes(cycleMonths)) cycleMonths = 12;
    const planYears = Math.max(1, Math.round(cycleMonths / 12));
    const year1Rate = isRenewal ? renewalRate : productRate;
    let base;
    if (cycleMonths < 12) {
      base = Math.round(year1Rate * (cycleMonths / 12) * 100) / 100;
    } else if (cycleMonths === 12) {
      base = Math.round(year1Rate * 100) / 100;
    } else {
      base = Math.round((year1Rate + renewalRate * (planYears - 1)) * 100) / 100;
    }
    let discount = 0;
    let discountLabel = "None";
    const val = Number(billForm.discount_value || 0);
    if (billForm.discount_mode === "percent" && val > 0) {
      discount = Math.min(base, Math.round(((base * Math.min(100, val)) / 100) * 100) / 100);
      discountLabel = `${Math.min(100, val)}%`;
    } else if (billForm.discount_mode === "flat" && val > 0) {
      discount = Math.min(base, val);
      discountLabel = formatMoney(discount, selectedProduct.currency);
    } else if (billForm.discount_mode === "promo" && billForm.promo_code.trim()) {
      discountLabel = `Promo ${billForm.promo_code.trim()} (server)`;
    }
    const taxPct = Number(selectedTax?.tax_value_percentage || 0);
    const total = Math.max(0, Math.round((base - discount) * 100) / 100);
    const taxAmount =
      taxPct > 0
        ? Math.round(((total * taxPct) / (100 + taxPct)) * 100) / 100
        : 0;
    const taxable = Math.round((total - taxAmount) * 100) / 100;
    const demoCredit = !isRenewal && billForm.apply_demo_credit ? demoDaysLeft : 0;
    const start = parseLocalDate(billForm.billing_start) || parseLocalDate(todayISO());
    const end = addMonths(start, cycleMonths);
    return {
      unitRate: year1Rate,
      renewalRate,
      planYears,
      base,
      discount,
      discountLabel,
      afterDiscount: total,
      taxable,
      taxPct,
      taxAmount,
      total,
      cycleMonths,
      start,
      end,
      demoCredit,
      currency: selectedProduct.currency,
      isRenewal,
      renewFromEnd: billedRenewalInfo.renewFromEnd,
    };
  }, [
    selectedProduct,
    billForm,
    selectedTax,
    demoDaysLeft,
    billedRenewalInfo.renewFromEnd,
    verifiedSchool,
  ]);

  useEffect(() => {
    if (!open || billForm.billing_start_touched) return;
    let iso;
    if (billForm.order_type === "Renewal" && billedRenewalInfo.renewFromEnd) {
      iso = billedRenewalInfo.renewFromEnd;
    } else {
      const orderD = parseLocalDate(billForm.order_date) || parseLocalDate(todayISO());
      const demo =
        billForm.order_type === "New" && billForm.apply_demo_credit
          ? demoDaysLeft
          : 0;
      const start = demo > 0 ? addDays(orderD, demo) : orderD;
      iso = toISODate(start);
    }
    setBillForm((f) => (f.billing_start === iso ? f : { ...f, billing_start: iso }));
  }, [
    open,
    billForm.order_date,
    billForm.apply_demo_credit,
    billForm.billing_start_touched,
    billForm.order_type,
    demoDaysLeft,
    billedRenewalInfo.renewFromEnd,
  ]);

  //Created By Cursor — rebuild EMI rows unless user edited them
  useEffect(() => {
    if (!open || !billPreview) return;
    const count = Math.max(1, Number(billForm.installment_count) || 1);
    const nextRows =
      count <= 1
        ? []
        : buildDefaultEmiRows(billPreview.total, count, billForm.order_date);
    setBillForm((f) => {
      if (count > 1 && f.emi_rows_touched && f.emi_rows?.length === count) {
        return f;
      }
      const prev = f.emi_rows || [];
      const same =
        prev.length === nextRows.length &&
        nextRows.every(
          (r, i) =>
            prev[i]?.due_date === r.due_date &&
            String(prev[i]?.amount) === String(r.amount)
        );
      if (same && !f.emi_rows_touched) return f;
      if (same && count <= 1) return f;
      return { ...f, emi_rows: nextRows, emi_rows_touched: false };
    });
  }, [open, billPreview, billForm.installment_count, billForm.order_date]);

  const emiSchedule = useMemo(() => {
    const rows = Array.isArray(billForm.emi_rows) ? billForm.emi_rows : [];
    if (rows.length <= 1 || !billPreview) return null;
    const mapped = rows.map((r, i) => ({
      seq: Number(r.seq) || i + 1,
      due_date: r.due_date,
      amount: Number(r.amount) || 0,
    }));
    const sum = Math.round(mapped.reduce((s, r) => s + r.amount, 0) * 100) / 100;
    return {
      count: mapped.length,
      currency: billPreview.currency,
      first: mapped[0],
      next: mapped[1] || null,
      rows: mapped,
      sum,
      total: Number(billPreview.total) || 0,
      sumOk: Math.abs(sum - (Number(billPreview.total) || 0)) < 0.02,
    };
  }, [billForm.emi_rows, billPreview]);

  useEffect(() => {
    if (!open || !billPreview || billForm.payment_amount_touched) return;
    //Created By Cursor — with EMI, default payment to 1st installment
    const nextAmt =
      emiSchedule?.first?.amount != null
        ? String(emiSchedule.first.amount)
        : String(billPreview.total ?? "");
    setBillForm((f) =>
      f.payment_amount === nextAmt ? f : { ...f, payment_amount: nextAmt }
    );
  }, [open, billPreview, billForm.payment_amount_touched, emiSchedule]);

  //Created By Cursor
  const updateEmiRow = (index, patch) => {
    setBillForm((f) => {
      const rows = (f.emi_rows || []).map((row, i) =>
        i === index ? { ...row, ...patch } : row
      );
      return { ...f, emi_rows: rows, emi_rows_touched: true };
    });
  };

  //Created By Cursor — default editable renewal value from subscription / catalog
  useEffect(() => {
    if (!open || billForm.renewal_rate_touched || !billForm.product_code) return;
    const schoolProd = (verifiedSchool?.products || []).find(
      (p) => p.product_code === billForm.product_code
    );
    const next =
      schoolProd?.renewal_rate ??
      selectedProduct?.renewal_rate ??
      schoolProd?.product_rate ??
      selectedProduct?.product_rate ??
      "";
    if (next === "" || next == null) return;
    const nextStr = String(next);
    setBillForm((f) =>
      f.renewal_rate === nextStr ? f : { ...f, renewal_rate: nextStr }
    );
  }, [
    open,
    billForm.product_code,
    billForm.order_type,
    billForm.renewal_rate_touched,
    verifiedSchool,
    selectedProduct,
  ]);

  //Created By Cursor — default plan months once; do not overwrite user Plan selection
  useEffect(() => {
    if (!open || !billForm.product_code || billForm.plan_months_touched) return;
    const schoolProd = (verifiedSchool?.products || []).find(
      (p) => p.product_code === billForm.product_code
    );
    let months = Number(
      schoolProd?.billing_cycle_months ||
        selectedProduct?.billing_cycle_months ||
        (String(selectedProduct?.validity || "").match(/^(\d+)\s*Year/i)
          ? Number(RegExp.$1) * 12
          : 12)
    );
    if (!PLAN_MONTH_VALUES.includes(months)) {
      months = months <= 3 ? 3 : months <= 6 ? 6 : 12;
      if (months > 12) {
        months = Math.min(60, Math.max(12, Math.round(months / 12) * 12));
        if (!PLAN_MONTH_VALUES.includes(months)) months = 12;
      }
    }
    setBillForm((f) =>
      Number(f.plan_months) === months ? f : { ...f, plan_months: months }
    );
  }, [
    open,
    billForm.product_code,
    billForm.plan_months_touched,
    verifiedSchool,
    selectedProduct,
  ]);

  const handleCreateBill = async () => {
    if (!billForm.school_code?.trim() || !billForm.product_code || !billForm.tax_id) {
      toast.error("School code, product and tax are required");
      return;
    }
    if (!verifiedSchool?.school) {
      toast.error("Verify the school before creating the bill");
      return;
    }
    if (billForm.record_payment) {
      const amt = Number(billForm.payment_amount);
      if (!amt || amt <= 0) {
        toast.error("Enter a valid payment amount");
        return;
      }
      if (!billForm.payment_method) {
        toast.error("Select a payment method");
        return;
      }
      if (!billForm.payment_date) {
        toast.error("Payment date is required");
        return;
      }
    }
    const renewalVal = Number(billForm.renewal_rate);
    if (billForm.renewal_rate === "" || Number.isNaN(renewalVal) || renewalVal < 0) {
      toast.error("Enter a valid renewal value");
      return;
    }
    const emiCount = Number(billForm.installment_count) || 1;
    let installmentSchedule = null;
    if (emiCount > 1) {
      const rows = Array.isArray(billForm.emi_rows) ? billForm.emi_rows : [];
      if (rows.length !== emiCount) {
        toast.error("Complete the EMI schedule before creating the bill");
        return;
      }
      for (let i = 0; i < rows.length; i += 1) {
        const due = String(rows[i].due_date || "").trim();
        const amt = Number(rows[i].amount);
        if (!due) {
          toast.error(`EMI #${i + 1}: due date required`);
          return;
        }
        if (!Number.isFinite(amt) || amt < 0) {
          toast.error(`EMI #${i + 1}: enter a valid amount`);
          return;
        }
      }
      const sum = Math.round(
        rows.reduce((s, r) => s + (Number(r.amount) || 0), 0) * 100
      ) / 100;
      const total = Number(billPreview?.total) || 0;
      if (Math.abs(sum - total) >= 0.02) {
        toast.error(
          `EMI amounts (${sum}) must equal bill total (${total})`
        );
        return;
      }
      installmentSchedule = rows.map((r, i) => ({
        seq: i + 1,
        due_date: String(r.due_date).slice(0, 10),
        amount_due: Number(r.amount) || 0,
      }));
    }

    const payload = {
      school_code: billForm.school_code.trim(),
      product_code: billForm.product_code,
      order_type: billForm.order_type,
      tax_id: billForm.tax_id,
      promo_code: "",
      promo_code_discount: 0,
      discount_percent: 0,
      installment_count: emiCount,
      billing_cycle_months: PLAN_MONTH_VALUES.includes(Number(billForm.plan_months))
        ? Number(billForm.plan_months)
        : 12,
      apply_demo_credit: billForm.billing_start_touched
        ? false
        : !!billForm.apply_demo_credit,
      order_date: billForm.order_date || todayISO(),
      service_start_date: billForm.billing_start || billForm.order_date || todayISO(),
      record_payment: !!billForm.record_payment,
      transaction_amount: billForm.payment_amount,
      payment_method: billForm.payment_method,
      payment_date: billForm.payment_date,
      payment_remark: billForm.payment_remark || "",
      payment_channel: "Manual",
      gateway_transaction_id: billForm.payment_reference?.trim() || "",
      renewal_rate: billForm.renewal_rate,
      installments: installmentSchedule,
    };
    if (billForm.discount_mode === "percent") {
      payload.discount_percent = Number(billForm.discount_value) || 0;
    } else if (billForm.discount_mode === "flat") {
      payload.promo_code_discount = Number(billForm.discount_value) || 0;
    } else if (billForm.discount_mode === "promo") {
      payload.promo_code = billForm.promo_code.trim();
    }

    setSaving(true);
    const result = await apiService.createBillWithPayment(payload);
    setSaving(false);

    if (!result.success) {
      toast.error(result.error || "Failed to create bill");
      return;
    }

    const order = result.data?.order || {};
    const payment = result.data?.payment || null;
    const orderId = order.order_id || "";
    const invoiceId =
      result.data?.invoice_id || payment?.invoice_id || order.invoice_id || "";
    const demoCredited = Number(order.demo_days_credited || 0);
    const svcStart = order.service_start_date
      ? formatShortDate(new Date(order.service_start_date))
      : "";
    const payOk = !!billForm.record_payment && !!payment && !payment.error;
    const sub = result.data?.subscription || payment?.subscription;
    const productAccount =
      result.data?.product_account || payment?.product_account;
    const processComplete = !!result.data?.process_complete;

    if (billForm.record_payment && payment?.error) {
      toast.error(
        payment.error ||
          `Bill ${orderId} created, but payment failed — record it below`
      );
      onCreated?.(result.data);
      onClose?.();
      return;
    }
    if (sub?.error) {
      toast.error(
        `Payment saved, but subscription not updated: ${sub.error}${
          sub.db_error ? ` (${sub.db_error})` : ""
        }`
      );
    } else if (productAccount?.error) {
      toast.error(
        `Subscription OK, but product account not updated: ${productAccount.error}`
      );
    }

    const subId = sub?.subscription_id;
    const acctNote =
      productAccount && !productAccount.error
        ? ` · account ${productAccount.product_status_name || "Active"}`
        : "";
    toast.success(
      orderId
        ? `Bill ${orderId}${invoiceId ? ` · ${invoiceId}` : ""} created${
            payOk ? " · payment recorded" : ""
          }${processComplete ? " · activated" : ""}${
            subId ? ` · ${subId}` : ""
          }${acctNote}${demoCredited && svcStart ? ` · cycle from ${svcStart}` : ""}`
        : "Bill created"
    );
    onCreated?.(result.data);
    onClose?.();
  };

  return (
      <Dialog
        open={open}
        onClose={() => !saving && handleClose()}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            border: `1px solid ${crmColors.border.default}`,
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            background: `linear-gradient(125deg, ${crmColors.primary.darker} 0%, ${crmColors.hero.start} 48%, ${crmColors.hero.mid} 100%)`,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            position: "relative",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              right: -40,
              top: -50,
              width: 160,
              height: 160,
              borderRadius: "50%",
              bgcolor: alpha("#fff", 0.06),
              pointerEvents: "none",
            },
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", position: "relative", zIndex: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                bgcolor: alpha("#fff", 0.16),
                border: `1px solid ${alpha("#fff", 0.22)}`,
              }}
            >
              <BillIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "1.12rem", lineHeight: 1.2 }}>
                Add new bill
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.88, mt: 0.2 }}>
                {!schoolVerified
                  ? "Step 1 · Verify school"
                  : !billForm.product_code
                    ? "Step 2 · Select product"
                    : "Step 3 · Complete bill & payment"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={() => !saving && handleClose()}
            sx={{
              color: "#fff",
              bgcolor: alpha("#fff", 0.12),
              border: `1px solid ${alpha("#fff", 0.18)}`,
              "&:hover": { bgcolor: alpha("#fff", 0.22) },
              position: "relative",
              zIndex: 1,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            px: { xs: 1.75, sm: 2.25 },
            py: 2,
            bgcolor: alpha(crmColors.primary.main, 0.02),
          }}
        >
          <Stack spacing={1.75}>
            <SectionPanel tone={schoolVerified ? "success" : "default"}>
              <SectionHead step="1" title="School" />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.15} alignItems={{ sm: "flex-start" }}>
                <TextField
                  label="School code"
                  size="small"
                  required
                  value={billForm.school_code}
                  onChange={(e) => handleSchoolCodeChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleVerifySchool();
                    }
                  }}
                  fullWidth
                  placeholder="Enter school code"
                  autoFocus
                  error={Boolean(verifyError)}
                  helperText={verifyError || undefined}
                  FormHelperTextProps={{ sx: { mx: 0, mt: 0.35 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SchoolIcon sx={{ fontSize: 18, color: crmColors.primary.main }} />
                      </InputAdornment>
                    ),
                    sx: fieldSx,
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => handleVerifySchool()}
                  disabled={verifying || !billForm.school_code.trim()}
                  startIcon={
                    verifying ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px",
                    minWidth: { sm: 118 },
                    height: 40,
                    flexShrink: 0,
                    bgcolor: crmColors.primary.main,
                    boxShadow: `0 4px 12px ${alpha(crmColors.primary.main, 0.28)}`,
                    "&:hover": { bgcolor: crmColors.primary.dark },
                  }}
                >
                  {verifying ? "Checking…" : "Verify"}
                </Button>
              </Stack>

              {schoolVerified ? (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.75,
                    borderRadius: "12px",
                    border: `1px solid ${alpha(crmColors.success.main, 0.25)}`,
                    background: `linear-gradient(135deg, ${alpha(crmColors.success.main, 0.06)} 0%, ${alpha(crmColors.primary.main, 0.04)} 100%)`,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                    <VerifiedIcon sx={{ color: crmColors.success.main, fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 800, fontSize: 14, color: crmColors.text.primary }}>
                      {verifiedSchool.school.school_name}
                    </Typography>
                    <Chip
                      label={verifiedSchool.school.school_code}
                      size="small"
                      sx={{
                        height: 20,
                        fontWeight: 700,
                        fontSize: 10.5,
                        bgcolor: alpha(crmColors.primary.main, 0.1),
                        color: crmColors.primary.main,
                      }}
                    />
                    {verifiedSchool.summary?.billing_status ? (
                      <Chip
                        label={verifiedSchool.summary.billing_status}
                        size="small"
                        sx={{
                          height: 20,
                          fontWeight: 700,
                          fontSize: 10.5,
                          bgcolor: alpha(crmColors.energy.main, 0.12),
                          color: crmColors.energy.dark,
                        }}
                      />
                    ) : null}
                  </Stack>
                  <Grid container spacing={1.25}>
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <PersonIcon sx={{ fontSize: 15, color: crmColors.text.secondary }} />
                        <Typography variant="body2" color="text.secondary">
                          {verifiedSchool.school.admin_name || "—"}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <PhoneIcon sx={{ fontSize: 15, color: crmColors.text.secondary }} />
                        <Typography variant="body2" color="text.secondary">
                          {verifiedSchool.school.admin_phone || "—"}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={0.75} alignItems="flex-start">
                        <PlaceIcon sx={{ fontSize: 15, color: crmColors.text.secondary, mt: 0.2 }} />
                        <Typography variant="body2" color="text.secondary">
                          {[
                            verifiedSchool.school.school_address,
                            verifiedSchool.school.district_name,
                            verifiedSchool.school.state,
                            verifiedSchool.school.country,
                          ]
                            .filter(Boolean)
                            .join(", ") || "—"}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{
                          mt: 0.25,
                          pt: 1,
                          borderTop: `1px dashed ${alpha(crmColors.energy.main, 0.35)}`,
                        }}
                      >
                        {!billForm.product_code && billForm.order_type !== "Upgrade" ? (
                          <Typography variant="caption" color="text.secondary">
                            Select a product below to check billing / demo status
                          </Typography>
                        ) : billedRenewalInfo.isBilled || billForm.order_type === "Upgrade" ? (
                          <>
                            <Chip
                              label={
                                billForm.order_type === "Upgrade"
                                  ? "Upgrade to Full System"
                                  : `Expiry ${billedRenewalInfo.expiryLabel || "—"}`
                              }
                              size="small"
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: 10.5,
                                bgcolor:
                                  billForm.order_type === "Upgrade"
                                    ? alpha(crmColors.primary.main, 0.12)
                                    : billedRenewalInfo.status === "Expired"
                                      ? alpha("#c62828", 0.12)
                                      : billedRenewalInfo.status === "Grace"
                                        ? alpha(crmColors.energy.main, 0.18)
                                        : alpha(crmColors.primary.main, 0.12),
                                color:
                                  billForm.order_type === "Upgrade"
                                    ? crmColors.primary.main
                                    : billedRenewalInfo.status === "Expired"
                                      ? "#c62828"
                                      : billedRenewalInfo.status === "Grace"
                                        ? crmColors.energy.dark
                                        : crmColors.primary.main,
                              }}
                            />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ flex: 1, minWidth: 180, fontSize: 12.5 }}
                            >
                              {billForm.order_type === "Upgrade"
                                ? "Timeline · App billed · upgrading to WebERP (TM004)"
                                : `Timeline · ${billedRenewalInfo.timelineLabel || "Billed"}${
                                    billedRenewalInfo.renewFromEnd
                                      ? ` · renews from ${formatShortDate(
                                          parseLocalDate(billedRenewalInfo.renewFromEnd)
                                        )}`
                                      : ""
                                  }`}
                            </Typography>
                            <Chip
                              label={`Rate ${formatMoney(
                                billForm.order_type === "Renewal"
                                  ? billedRenewalInfo.product?.renewal_rate ??
                                      selectedProduct?.renewal_rate ??
                                      billedRenewalInfo.product?.product_rate ??
                                      selectedProduct?.product_rate
                                  : selectedProduct?.product_rate ??
                                      selectedProduct?.renewal_rate,
                                selectedProduct?.currency || "INR"
                              )}`}
                              size="small"
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: 10.5,
                                bgcolor: alpha(crmColors.energy.main, 0.14),
                                color: crmColors.energy.dark,
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <Chip
                              label={
                                demoDaysLeft > 0
                                  ? `${demoDaysLeft} demo day${demoDaysLeft === 1 ? "" : "s"} left`
                                  : "No demo days left"
                              }
                              size="small"
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: 10.5,
                                bgcolor:
                                  demoDaysLeft > 0
                                    ? alpha(crmColors.energy.main, 0.18)
                                    : alpha(crmColors.text.secondary, 0.1),
                                color:
                                  demoDaysLeft > 0
                                    ? crmColors.energy.dark
                                    : crmColors.text.secondary,
                              }}
                            />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ flex: 1, minWidth: 160, fontSize: 12.5 }}
                            >
                              {demoDaysLeft > 0
                                ? `Billing cycle starts after ${demoDaysLeft} demo day${demoDaysLeft === 1 ? "" : "s"} (not today).`
                                : "Billing cycle starts from today."}
                            </Typography>
                            <Button
                              size="small"
                              variant={billForm.apply_demo_credit ? "contained" : "outlined"}
                              onClick={() =>
                                setBillForm((f) => {
                                  const next = !f.apply_demo_credit;
                                  return {
                                    ...f,
                                    apply_demo_credit: next,
                                    billing_start_touched: next
                                      ? false
                                      : f.billing_start_touched,
                                  };
                                })
                              }
                              disabled={demoDaysLeft <= 0}
                              sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                borderRadius: "8px",
                                bgcolor: billForm.apply_demo_credit
                                  ? crmColors.energy.main
                                  : undefined,
                                "&:hover": {
                                  bgcolor: billForm.apply_demo_credit
                                    ? crmColors.energy.dark
                                    : undefined,
                                },
                              }}
                            >
                              {billForm.apply_demo_credit
                                ? "Demo credit ON"
                                : "Demo credit OFF"}
                            </Button>
                          </>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              ) : null}
            </SectionPanel>

            {schoolVerified ? (
            <SectionPanel>
              <SectionHead step="2" title="Bill type" />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                {[
                  {
                    value: "New",
                    label: "New",
                    enabled: !schoolIsBilled,
                    hint: schoolIsBilled
                      ? "Cancel current subscription from School Details first"
                      : "",
                  },
                  {
                    value: "Renewal",
                    label: "Renewal",
                    enabled: schoolIsBilled,
                    hint: !schoolIsBilled ? "Only for billed schools" : "",
                  },
                  {
                    value: "Upgrade",
                    label: "Upgrade",
                    enabled: canUpgradeToFullSystem,
                    hint: !canUpgradeToFullSystem
                      ? "Only when Android App is billed (not Full System)"
                      : "",
                  },
                ].map((t) => {
                  const selected = billForm.order_type === t.value;
                  const disabled = !t.enabled;
                  return (
                    <Chip
                      key={t.value}
                      label={t.label}
                      clickable={!disabled}
                      disabled={disabled}
                      title={disabled ? t.hint : undefined}
                      onClick={() => {
                        if (disabled) return;
                        handleBillTypeChange(t.value);
                      }}
                      color={selected && !disabled ? "primary" : "default"}
                      variant={selected && !disabled ? "filled" : "outlined"}
                      sx={{
                        fontWeight: 700,
                        height: 32,
                        px: 0.5,
                        opacity: disabled ? 0.55 : 1,
                        ...(selected && !disabled
                          ? {
                              bgcolor: crmColors.primary.main,
                              color: "#fff",
                              "&:hover": { bgcolor: crmColors.primary.dark },
                            }
                          : {
                              borderColor: alpha(crmColors.primary.main, 0.35),
                            }),
                        "&.Mui-disabled": {
                          opacity: 0.45,
                          borderColor: alpha(crmColors.text.secondary, 0.25),
                          color: crmColors.text.secondary,
                        },
                      }}
                    />
                  );
                })}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {billForm.order_type === "Upgrade"
                  ? "Upgrade Android App → Full System (WebERP)"
                  : billForm.order_type === "Renewal"
                    ? "Renew billed product · product locked (cancel from School Details to start new)"
                    : "New subscription for this school"}
              </Typography>
            </SectionPanel>
            ) : null}

            {schoolVerified ? (
            <SectionPanel>
              <SectionHead step="3" title="Product" />
              <TextField
                select
                label="Product"
                size="small"
                required
                value={billForm.product_code}
                disabled={schoolIsBilled}
                onChange={(e) => {
                  //Created By Cursor — product locked while billed; use Upgrade chip for TM004
                  if (schoolIsBilled) {
                    toast.error(
                      "Product locked while billed. Cancel subscription from School Details to change."
                    );
                    return;
                  }
                  setBillForm((f) => ({
                    ...f,
                    product_code: e.target.value,
                    renewal_rate_touched: false,
                    plan_months_touched: false,
                  }));
                }}
                fullWidth
                InputProps={{ sx: fieldSx }}
                helperText={
                  billForm.order_type === "Upgrade"
                    ? "Full System (TM004) via Upgrade"
                    : schoolIsBilled
                      ? "Product locked · cancel from School Details to start a new subscription"
                      : undefined
                }
                FormHelperTextProps={{ sx: { mx: 0, mt: 0.3 } }}
              >
                {products.map((p) => {
                  const schoolProd = (verifiedSchool?.products || []).find(
                    (sp) => sp.product_code === p.product_code
                  );
                  const rate =
                    billForm.order_type === "Renewal"
                      ? schoolProd?.renewal_rate ?? p.renewal_rate
                      : p.product_rate;
                  return (
                    <MenuItem key={`${p.product_code}-${p.rate_id || ""}`} value={p.product_code}>
                      {p.product_name} ({p.product_code}) — {formatMoney(rate, p.currency)}
                      {billForm.order_type === "Renewal" ? " renewal" : ""}
                    </MenuItem>
                  );
                })}
              </TextField>
            </SectionPanel>
            ) : null}

            {schoolVerified && billForm.product_code ? (
            <>
            <SectionPanel>
              <SectionHead step="4" title="Bill details" />
              <Grid container spacing={1.25} sx={{ mb: 1.25 }}>
                <Grid item xs={6} sm={2}>
                  <TextField
                    select
                    label="Tax"
                    size="small"
                    required
                    value={billForm.tax_id}
                    onChange={(e) => setBillForm((f) => ({ ...f, tax_id: e.target.value }))}
                    fullWidth
                    InputProps={{ sx: fieldSx }}
                  >
                    {taxes.map((t) => (
                      <MenuItem key={t.tax_id} value={t.tax_id}>
                        {t.tax_name} ({t.tax_value_percentage}%)
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    select
                    label="EMI"
                    size="small"
                    value={Number(billForm.installment_count) || 1}
                    onChange={(e) =>
                      setBillForm((f) => ({
                        ...f,
                        installment_count: Number(e.target.value) || 1,
                        payment_amount_touched: false,
                        emi_rows_touched: false,
                      }))
                    }
                    fullWidth
                    helperText={
                      emiSchedule
                        ? `1st ${formatMoney(emiSchedule.first.amount, emiSchedule.currency)}`
                        : "Full amount in one go"
                    }
                    FormHelperTextProps={{ sx: { mx: 0, mt: 0.3 } }}
                    InputProps={{ sx: fieldSx }}
                  >
                    {EMI_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    select
                    label="Discount"
                    size="small"
                    value={billForm.discount_mode}
                    onChange={(e) =>
                      setBillForm((f) => ({
                        ...f,
                        discount_mode: e.target.value,
                        discount_value: "",
                        promo_code: e.target.value === "promo" ? f.promo_code : "",
                      }))
                    }
                    fullWidth
                    InputProps={{ sx: fieldSx }}
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="percent">Percent %</MenuItem>
                    <MenuItem value="flat">Flat ₹</MenuItem>
                    <MenuItem value="promo">Promo</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  {billForm.discount_mode === "promo" ? (
                    <TextField
                      label="Promo code"
                      size="small"
                      value={billForm.promo_code}
                      onChange={(e) =>
                        setBillForm((f) => ({ ...f, promo_code: e.target.value }))
                      }
                      fullWidth
                      InputProps={{ sx: fieldSx }}
                    />
                  ) : billForm.discount_mode === "none" ? (
                    <TextField
                      size="small"
                      fullWidth
                      disabled
                      value="—"
                      label="Amount"
                      InputProps={{ sx: fieldSx }}
                    />
                  ) : (
                    <TextField
                      label={
                        billForm.discount_mode === "percent" ? "Discount %" : "Discount ₹"
                      }
                      type="number"
                      size="small"
                      value={billForm.discount_value}
                      onChange={(e) =>
                        setBillForm((f) => ({ ...f, discount_value: e.target.value }))
                      }
                      fullWidth
                      inputProps={{
                        min: 0,
                        max: billForm.discount_mode === "percent" ? 100 : undefined,
                        step: billForm.discount_mode === "percent" ? 1 : 0.01,
                      }}
                      InputProps={{ sx: fieldSx }}
                    />
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    select
                    label="Plan"
                    size="small"
                    required
                    value={
                      PLAN_MONTH_VALUES.includes(Number(billForm.plan_months))
                        ? Number(billForm.plan_months)
                        : 12
                    }
                    onChange={(e) =>
                      setBillForm((f) => ({
                        ...f,
                        plan_months: Number(e.target.value) || 12,
                        plan_months_touched: true,
                      }))
                    }
                    fullWidth
                    helperText={
                      billPreview
                        ? billPreview.cycleMonths < 12
                          ? `${billPreview.cycleMonths} mo pro-rata ${formatMoney(billPreview.unitRate, billPreview.currency)}/yr = ${formatMoney(billPreview.base, billPreview.currency)}`
                          : billPreview.cycleMonths === 12
                            ? formatMoney(billPreview.base, billPreview.currency)
                            : billPreview.planYears === 2
                              ? `Y1 ${formatMoney(billPreview.unitRate, billPreview.currency)} + Y2 @ ${formatMoney(billPreview.renewalRate, billPreview.currency)} = ${formatMoney(billPreview.base, billPreview.currency)}`
                              : `Y1 ${formatMoney(billPreview.unitRate, billPreview.currency)} + Y2–${billPreview.planYears} @ ${formatMoney(billPreview.renewalRate, billPreview.currency)} = ${formatMoney(billPreview.base, billPreview.currency)}`
                        : "3/6 mo pro-rata; Y2+ uses renewal value"
                    }
                    FormHelperTextProps={{ sx: { mx: 0, mt: 0.3 } }}
                    InputProps={{ sx: fieldSx }}
                  >
                    {PLAN_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              {emiSchedule ? (
                <Box
                  sx={{
                    mt: 1,
                    mb: 1.5,
                    px: 1,
                    py: 0.75,
                    borderRadius: "10px",
                    border: `1px solid ${alpha(crmColors.primary.main, 0.2)}`,
                    background: alpha(crmColors.primary.main, 0.03),
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase" }}
                    >
                      EMI schedule
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: emiSchedule.sumOk ? "success.main" : "error.main",
                      }}
                    >
                      {formatMoney(emiSchedule.sum, emiSchedule.currency)}
                      {" / "}
                      {formatMoney(emiSchedule.total, emiSchedule.currency)}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "28px 1fr 1fr",
                      gap: 0.5,
                      alignItems: "center",
                      mb: 0.35,
                      px: 0.25,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      #
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Due
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Amount
                    </Typography>
                  </Box>
                  <Stack spacing={0.4}>
                    {(billForm.emi_rows || []).map((row, index) => (
                      <Box
                        key={`emi-${row.seq || index}`}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "28px 1fr 1fr",
                          gap: 0.5,
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 800, textAlign: "center" }}
                        >
                          {index + 1}
                        </Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={row.due_date || ""}
                          onChange={(e) =>
                            updateEmiRow(index, { due_date: e.target.value })
                          }
                          InputProps={{
                            sx: {
                              ...fieldSx,
                              "& .MuiInputBase-input": { py: 0.55, fontSize: 12.5 },
                            },
                          }}
                        />
                        <TextField
                          type="number"
                          size="small"
                          fullWidth
                          value={row.amount}
                          onChange={(e) =>
                            updateEmiRow(index, { amount: e.target.value })
                          }
                          inputProps={{ min: 0, step: 0.01 }}
                          InputProps={{
                            sx: {
                              ...fieldSx,
                              "& .MuiInputBase-input": { py: 0.55, fontSize: 12.5 },
                            },
                            startAdornment: (
                              <InputAdornment position="start" sx={{ mr: 0.25 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                  ₹
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ) : null}

              <Grid container spacing={1.25}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Order date"
                    type="date"
                    size="small"
                    required
                    InputLabelProps={{ shrink: true }}
                    value={billForm.order_date}
                    onChange={(e) =>
                      setBillForm((f) => ({
                        ...f,
                        order_date: e.target.value,
                        billing_start_touched: false,
                        emi_rows_touched: false,
                      }))
                    }
                    fullWidth
                    InputProps={{ sx: fieldSx }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Billing start"
                    type="date"
                    size="small"
                    required
                    InputLabelProps={{ shrink: true }}
                    value={billForm.billing_start}
                    onChange={(e) =>
                      setBillForm((f) => ({
                        ...f,
                        billing_start: e.target.value,
                        billing_start_touched: true,
                        apply_demo_credit: false,
                      }))
                    }
                    fullWidth
                    helperText={
                      billForm.billing_start_touched
                        ? "Manual start"
                        : billForm.order_type === "Renewal" &&
                            billedRenewalInfo.renewFromEnd
                          ? "Renew from current end"
                          : billForm.order_type === "Renewal"
                            ? "Renew from order date"
                            : billForm.order_type === "Upgrade"
                              ? "Upgrade cycle from order date"
                              : billPreview?.demoCredit > 0
                                ? `Auto +${billPreview.demoCredit}d demo`
                                : "From order date"
                    }
                    FormHelperTextProps={{ sx: { mx: 0, mt: 0.3 } }}
                    InputProps={{ sx: fieldSx }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Billing end"
                    size="small"
                    value={billPreview ? formatShortDate(billPreview.end) : "—"}
                    fullWidth
                    disabled
                    helperText={
                      billPreview
                        ? `${
                            PLAN_OPTIONS.find((o) => o.months === billPreview.cycleMonths)
                              ?.label || `${billPreview.cycleMonths} mo`
                          } plan`
                        : "Select product / plan"
                    }
                    FormHelperTextProps={{ sx: { mx: 0, mt: 0.3 } }}
                    InputProps={{ sx: fieldSx }}
                  />
                </Grid>
              </Grid>

              {billPreview ? (
                <Box
                  sx={{
                    mt: 1.35,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(5, 1fr)" },
                    gap: 0,
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: `1px solid ${alpha(crmColors.primary.main, 0.16)}`,
                    background: `linear-gradient(120deg, ${alpha(crmColors.primary.main, 0.05)} 0%, ${alpha(crmColors.energy.main, 0.06)} 100%)`,
                  }}
                >
                  {[
                    { label: "Base", value: formatMoney(billPreview.base, billPreview.currency) },
                    {
                      label: "Discount",
                      value:
                        billPreview.discount > 0
                          ? `−${formatMoney(billPreview.discount, billPreview.currency)}`
                          : "—",
                    },
                    {
                      label: "Taxable",
                      value: formatMoney(billPreview.taxable, billPreview.currency),
                    },
                    {
                      label: `Tax ${billPreview.taxPct}%`,
                      value: formatMoney(billPreview.taxAmount, billPreview.currency),
                      sub: "reverse",
                    },
                    {
                      label: "Total",
                      value: formatMoney(billPreview.total, billPreview.currency),
                      emphasize: true,
                    },
                  ].map((cell, idx) => (
                    <Box
                      key={cell.label}
                      sx={{
                        px: 1.15,
                        py: 1.05,
                        borderLeft:
                          idx === 0
                            ? "none"
                            : `1px solid ${alpha(crmColors.primary.main, 0.1)}`,
                        bgcolor: cell.emphasize
                          ? alpha(crmColors.primary.main, 0.08)
                          : "transparent",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 9.5,
                          fontWeight: 800,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: crmColors.text.secondary,
                          lineHeight: 1.2,
                        }}
                      >
                        {cell.label}
                        {cell.sub ? (
                          <Box
                            component="span"
                            sx={{ ml: 0.4, fontWeight: 600, textTransform: "none" }}
                          >
                            ({cell.sub})
                          </Box>
                        ) : null}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: cell.emphasize ? 16.5 : 13.5,
                          color: cell.emphasize
                            ? crmColors.primary.main
                            : crmColors.text.primary,
                          lineHeight: 1.25,
                          mt: 0.25,
                        }}
                      >
                        {cell.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : null}
            </SectionPanel>

            <SectionPanel tone="energy">
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: billForm.record_payment ? 1.15 : 0 }}
              >
                <SectionHead
                  step="4"
                  title="Manual payment"
                  sx={{ mb: 0, flex: 1 }}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    label="Manual"
                    sx={{
                      height: 22,
                      fontWeight: 700,
                      bgcolor: alpha(crmColors.primary.main, 0.12),
                      color: crmColors.primary.main,
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={!!billForm.record_payment}
                        onChange={(e) =>
                          setBillForm((f) => ({ ...f, record_payment: e.target.checked }))
                        }
                        color="warning"
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>
                        {billForm.record_payment ? "Pay now" : "Pay later"}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Stack>
              </Stack>

              <Grid container spacing={1.25}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Payment amount"
                    type="number"
                    size="small"
                    required={!!billForm.record_payment}
                    disabled={!billForm.record_payment}
                    value={billForm.payment_amount}
                    onChange={(e) =>
                      setBillForm((f) => ({
                        ...f,
                        payment_amount: e.target.value,
                        payment_amount_touched: true,
                      }))
                    }
                    fullWidth
                    helperText={
                      billForm.record_payment
                        ? emiSchedule
                          ? `1st EMI ${formatMoney(emiSchedule.first.amount, emiSchedule.currency)} · total ${formatMoney(billPreview.total, billPreview.currency)}`
                          : billPreview
                            ? `Total due ${formatMoney(billPreview.total, billPreview.currency)}`
                            : "Amount received"
                        : "Pay later"
                    }
                    FormHelperTextProps={{ sx: { mx: 0, mt: 0.3 } }}
                    InputProps={{
                      sx: fieldSx,
                      startAdornment: (
                        <InputAdornment position="start">
                          <PaymentIcon
                            sx={{ fontSize: 18, color: crmColors.text.secondary }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Renewal value"
                    type="number"
                    size="small"
                    required
                    value={billForm.renewal_rate}
                    onChange={(e) =>
                      setBillForm((f) => ({
                        ...f,
                        renewal_rate: e.target.value,
                        renewal_rate_touched: true,
                      }))
                    }
                    fullWidth
                    helperText="Saved on order & subscription"
                    FormHelperTextProps={{ sx: { mx: 0, mt: 0.3 } }}
                    InputProps={{
                      sx: fieldSx,
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Payment mode"
                    size="small"
                    required={!!billForm.record_payment}
                    disabled={!billForm.record_payment}
                    value={billForm.payment_method}
                    onChange={(e) =>
                      setBillForm((f) => ({ ...f, payment_method: e.target.value }))
                    }
                    fullWidth
                    InputProps={{ sx: fieldSx }}
                  >
                    {(payMethods.length
                      ? payMethods
                      : [
                          { payment_mode_id: "Online", payment_mode_name: "Online" },
                          { payment_mode_id: "Transfer", payment_mode_name: "Transfer" },
                          { payment_mode_id: "Offline", payment_mode_name: "Offline" },
                        ]
                    ).map((m) => (
                      <MenuItem key={m.payment_mode_id} value={String(m.payment_mode_id)}>
                        {m.payment_mode_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {billForm.record_payment ? (
                  <>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Payment date"
                        type="date"
                        size="small"
                        required
                        InputLabelProps={{ shrink: true }}
                        value={billForm.payment_date}
                        onChange={(e) =>
                          setBillForm((f) => ({ ...f, payment_date: e.target.value }))
                        }
                        fullWidth
                        InputProps={{ sx: fieldSx }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Reference txn ID"
                        size="small"
                        value={billForm.payment_reference}
                        onChange={(e) =>
                          setBillForm((f) => ({ ...f, payment_reference: e.target.value }))
                        }
                        fullWidth
                        placeholder="UTR / bank / gateway ref"
                        InputProps={{ sx: fieldSx }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Remark"
                        size="small"
                        value={billForm.payment_remark}
                        onChange={(e) =>
                          setBillForm((f) => ({ ...f, payment_remark: e.target.value }))
                        }
                        fullWidth
                        placeholder="Optional note"
                        InputProps={{ sx: fieldSx }}
                      />
                    </Grid>
                  </>
                ) : null}
              </Grid>
            </SectionPanel>
            </>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            bgcolor: alpha(crmColors.primary.main, 0.03),
            borderTop: `1px solid ${crmColors.border.default}`,
          }}
        >
          <Button
            onClick={handleClose}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}
          >
            Cancel
          </Button>
          {schoolVerified && billForm.product_code ? (
          <Button
            variant="contained"
            onClick={handleCreateBill}
            disabled={saving || !billForm.tax_id}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : billForm.record_payment ? (
                <PaymentIcon />
              ) : (
                <AddIcon />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              px: 2.5,
              bgcolor: crmColors.energy.main,
              "&:hover": { bgcolor: crmColors.energy.dark },
              "&.Mui-disabled": { bgcolor: alpha(crmColors.energy.main, 0.35), color: "#fff" },
            }}
          >
            {saving
              ? billForm.record_payment
                ? "Creating & paying…"
                : "Creating…"
              : billForm.record_payment
                ? "Create bill & record payment"
                : "Create bill only"}
          </Button>
          ) : null}
        </DialogActions>
      </Dialog>
  );
};

export default MdlAddNewBill;
